// Builder levels as compact text ("BHL1." + deflated JSON in base64url), and the clean-up every
// level from someone else goes through: only known fields, sane numbers, sizes and strings.

import { deflateSync, inflateSync, strFromU8, strToU8 } from 'fflate';
import { SHAPES, type Shape } from '../maps/boxlevel/shapes';
import {
  DEFAULT_THEME,
  MATERIALS,
  validateLevel,
  type BoxKind,
  type LevelBox,
  type LevelDef,
  type LevelProp,
  type LevelTheme,
  type MaterialName,
  type Vec3Tuple,
} from '../maps/boxlevel/types';

export const LEVEL_CODE_PREFIX = 'BHL1.';
export const MAX_PIECES = 5000;
export const MAX_PROPS = 2000;
/** Coordinates further out than this are refused (the longest built-in maps reach about 100000). */
const MAX_COORD = 200000;

const KINDS: readonly BoxKind[] = [
  'solid',
  'block',
  'start',
  'checkpoint',
  'end',
  'boost',
  'deco',
  'pad',
  'ladder',
  'water',
  'wind',
  'gravity',
];
const SHAPE_IDS = new Set<string>(SHAPES.map((s) => s.id));
const COLOR = /^#[0-9a-f]{6}$/i;

function num(v: unknown, min: number, max: number, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : fallback;
}

function vec(v: unknown): Vec3Tuple | null {
  if (!Array.isArray(v) || v.length !== 3) return null;
  if (!v.every((n) => typeof n === 'number' && Number.isFinite(n) && Math.abs(n) <= MAX_COORD)) return null;
  return [v[0], v[1], v[2]];
}

function color(v: unknown, fallback: string): string {
  return typeof v === 'string' && COLOR.test(v) ? v.toLowerCase() : fallback;
}

function material(v: unknown): MaterialName {
  if (typeof v === 'string') {
    if ((MATERIALS as readonly string[]).includes(v)) return v as MaterialName;
    if (/^img:[a-z0-9_-]{1,64}$/i.test(v)) return v as MaterialName;
  }
  return 'grid';
}

function cleanTheme(t: unknown): LevelTheme {
  const o = (t && typeof t === 'object' ? t : {}) as Partial<LevelTheme>;
  const d = DEFAULT_THEME;
  return {
    skyTop: color(o.skyTop, d.skyTop),
    skyHorizon: color(o.skyHorizon, d.skyHorizon),
    fog: color(o.fog, d.fog),
    fogNear: num(o.fogNear, 0, 200000, d.fogNear),
    fogFar: num(o.fogFar, 1, 400000, d.fogFar),
    sun: color(o.sun, d.sun),
    sunStrength: num(o.sunStrength, 0, 2, d.sunStrength),
    ambient: num(o.ambient, 0, 2, d.ambient),
    voidColor: color(o.voidColor, d.voidColor),
  };
}

function cleanBox(b: unknown): LevelBox | null {
  if (!b || typeof b !== 'object') return null;
  const o = b as Partial<LevelBox>;
  const min = vec(o.min);
  const max = vec(o.max);
  if (!min || !max || min[0] >= max[0] || min[1] >= max[1] || min[2] >= max[2]) return null;
  const kind = KINDS.includes(o.kind as BoxKind) ? (o.kind as BoxKind) : 'solid';
  const box: LevelBox = { min, max, kind, material: material(o.material), color: color(o.color, '#888888') };
  if (kind === 'boost' || kind === 'pad' || kind === 'wind') {
    const boost = vec(o.boost);
    if (boost)
      box.boost = [
        num(boost[0], -4000, 4000, 0),
        num(boost[1], -4000, 4000, 0),
        num(boost[2], -4000, 4000, 0),
      ];
  }
  if (kind === 'gravity') box.gravity = num(o.gravity, 0.1, 3, 0.5);
  if (typeof o.spin === 'number' && Number.isFinite(o.spin) && o.spin !== 0)
    box.spin = num(o.spin, -720, 720, 0);
  if (o.blink && typeof o.blink === 'object') {
    const on = num(o.blink.on, 0.1, 60, 0);
    const off = num(o.blink.off, 0.1, 60, 0);
    if (on > 0 && off > 0) box.blink = { on, off, phase: num(o.blink.phase, 0, 1, 0) };
  }
  if (typeof o.shape === 'string' && SHAPE_IDS.has(o.shape)) box.shape = o.shape as Shape;
  if (typeof o.turn === 'number' && Number.isInteger(o.turn)) box.turn = ((o.turn % 4) + 4) % 4;
  if (o.move && typeof o.move === 'object') {
    const offset = vec(o.move.offset);
    const period = num(o.move.period, 0.2, 600, 0);
    if (offset && period > 0) {
      box.move = { offset, period };
      if (o.move.phase !== undefined) box.move.phase = num(o.move.phase, 0, 1, 0);
    }
  }
  return box;
}

function cleanProp(p: unknown): LevelProp | null {
  if (!p || typeof p !== 'object') return null;
  const o = p as Partial<LevelProp>;
  const pos = vec(o.pos);
  if (!pos || typeof o.model !== 'string' || !/^[a-z0-9_./-]{1,80}$/i.test(o.model)) return null;
  return { model: o.model, pos, yaw: num(o.yaw, -1000, 1000, 0), scale: num(o.scale, 0.01, 1000, 1) };
}

/**
 * A level from outside (a code, a file, the server), reduced to known fields with sane values.
 * Throws with a readable message if it can't be played.
 */
export function sanitizeLevel(v: unknown, name: string): LevelDef {
  const err = validateLevel(v && typeof v === 'object' ? { ...v, name } : v);
  if (err) throw new Error(err);
  const o = v as LevelDef;
  if (o.boxes.length > MAX_PIECES) throw new Error(`The level has more than ${MAX_PIECES} pieces.`);
  const boxes = o.boxes.map(cleanBox);
  if (boxes.some((b) => !b)) throw new Error('A piece of the level is broken.');
  const spawn = vec(o.spawn.pos)!;
  const def: LevelDef = {
    v: 1,
    name,
    title: cleanTitle(o.title) || 'Untitled',
    difficulty: Math.round(num(o.difficulty, 1, 5, 3)),
    theme: cleanTheme(o.theme),
    boxes: boxes as LevelBox[],
    spawn: { pos: spawn, yaw: num(o.spawn.yaw, -1000, 1000, 0) },
    failZ: num(o.failZ, -MAX_COORD, MAX_COORD, -1000),
  };
  if (typeof o.author === 'string' && o.author.trim()) def.author = cleanTitle(o.author).slice(0, 24);
  if (Array.isArray(o.props) && o.props.length) {
    def.props = o.props
      .slice(0, MAX_PROPS)
      .map(cleanProp)
      .filter((p): p is LevelProp => !!p);
    if (!def.props.length) delete def.props;
  }
  return def;
}

/** Printable text only, one line, at most 40 characters. */
export function cleanTitle(v: unknown): string {
  if (typeof v !== 'string') return '';
  return v
    .replace(/\s+/g, ' ')
    .replace(/[\p{C}]/gu, '')
    .trim()
    .slice(0, 40);
}

/** The level as it is shared: no local id. */
function shareable(def: LevelDef): Omit<LevelDef, 'name'> {
  const { name: _name, ...rest } = def;
  void _name;
  return rest;
}

/** Level -> deflated JSON bytes (what the server stores). */
export function packLevel(def: LevelDef): Uint8Array {
  return deflateSync(strToU8(JSON.stringify(shareable(def))), { level: 9 });
}

/** Deflated JSON bytes -> clean level. Throws with a readable message. */
export function unpackLevel(data: Uint8Array, name: string): LevelDef {
  let json: unknown;
  try {
    // Refuse anything that inflates past 8 MB (a level of 5000 pieces is well under 2 MB).
    const raw = inflateSync(data, { out: new Uint8Array(8 * 1024 * 1024) });
    json = JSON.parse(strFromU8(raw));
  } catch {
    throw new Error("That isn't a level.");
  }
  return sanitizeLevel({ ...(json as object), name }, name);
}

export function toBase64Url(b: Uint8Array): string {
  let s = '';
  for (let i = 0; i < b.length; i += 0x8000) s += String.fromCharCode(...b.subarray(i, i + 0x8000));
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromBase64Url(s: string): Uint8Array {
  const t = s.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(t + '='.repeat((4 - (t.length % 4)) % 4));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Level -> "BHL1.…" text, for pasting anywhere. */
export function levelToCode(def: LevelDef): string {
  return LEVEL_CODE_PREFIX + toBase64Url(packLevel(def));
}

/** "BHL1.…" text -> level. Throws with a readable message. */
export function levelFromCode(code: string, name: string): LevelDef {
  const t = code.trim().replace(/\s+/g, '');
  if (!t.startsWith(LEVEL_CODE_PREFIX)) throw new Error("That isn't a level code.");
  let bytes: Uint8Array;
  try {
    bytes = fromBase64Url(t.slice(LEVEL_CODE_PREFIX.length));
  } catch {
    throw new Error('The level code is damaged (copy all of it).');
  }
  return unpackLevel(bytes, name);
}

/** Short published-map code: 6 characters without look-alikes (0/O, 1/I/L, U/V). */
export const SHARE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTWXYZ';
export function normalizeShareCode(s: string): string | null {
  const ok = (t: string) => t.length === 6 && [...t].every((c) => SHARE_ALPHABET.includes(c));
  const t = s.trim().toUpperCase().replace(/[\s-]/g, '');
  if (ok(t)) return t;
  return t.startsWith('BH') && ok(t.slice(2)) ? t.slice(2) : null;
}

/**
 * Fingerprint of what a level plays like (pieces, spawn, fail height; not its name or look), to
 * tell whether a finished run still belongs to the level after edits.
 */
export function levelKey(def: LevelDef): string {
  const s = JSON.stringify({ b: def.boxes, s: def.spawn, f: def.failZ });
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193);
    h2 = Math.imul(h2 ^ c, 0x5bd1e995);
  }
  return (h1 >>> 0).toString(16).padStart(8, '0') + (h2 >>> 0).toString(16).padStart(8, '0') + s.length;
}

/** The level exactly as the server will see it (same JSON round trip and clean-up). */
export function canonicalLevel(def: LevelDef): LevelDef {
  return unpackLevel(packLevel(def), def.name);
}
